/**
 * The programme at a glance: how many of the farmers on the record have been
 * reached, where each village stands, and what needs a visit - pins outside
 * the record, pins in someone else's khasra, farmers who stopped mid-way.
 */
import { useEffect, useMemo, useState } from 'react';
import type { OnboardingEvent, Stage } from '@shared/model';
import { STAGES, STAGE_COLOR, STAGE_LABEL, prettyPhone } from '@shared/model';
import { apiGet } from '../api/client';
import { Funnel } from '../components/Funnel';
import { StagePill } from '../components/StagePill';
import { fmtDate } from '../components/FarmerCard';
import { StateBlock } from '../components/StateBlock';
import { useRoster } from '../state/RosterContext';
import { useSession } from '../state/SessionContext';
import { loadBlock, type BlockSummary, type PlotProps } from './Block';

const n = (x: number): string => x.toLocaleString('en-IN');
const DAY = 86_400_000;

export function Dashboard(): JSX.Element {
  const { holders, summary, villages, reached, rows, byId, farmIndex, unmatched, lastSync, sync, whatsapp, reload } = useRoster();
  const { account } = useSession();
  const [events, setEvents] = useState<OnboardingEvent[]>([]);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [block, setBlock] = useState<{ summary: BlockSummary | null; plots: PlotProps[] } | null>(null);
  useEffect(() => {
    void loadBlock().then(([fc, s]) => setBlock({ summary: s, plots: fc.features.map((f) => f.properties as unknown as PlotProps) })).catch(() => setBlock(null));
  }, []);
  const blockStages = useMemo(() => {
    const c = { phone: 0, joined: 0, activated: 0 };
    for (const p of block?.plots ?? []) {
      const st = rows.get(p.holder_id)?.stage ?? 'target';
      if (st !== 'target') c.phone++;
      if (['joined', 'pin', 'boundary', 'confirmed', 'activated'].includes(st)) c.joined++;
      if (st === 'activated') c.activated++;
    }
    return c;
  }, [block, rows]);

  // The monitor is what people come back to: re-read the onboarding records
  // every time it opens, so a colleague's entries and a sync show up.
  useEffect(reload, [reload]);

  useEffect(() => {
    void apiGet<OnboardingEvent[]>('/api/events?limit=40').then((r) => r.status === 'ready' && r.data && setEvents(r.data));
  }, [rows]);

  const attention = useMemo(() => {
    const list = [...rows.values()];
    const now = Date.now();
    const lastTouch = (r: (typeof list)[number]): number => {
      const ts = [r.last_message_at, r.pin_at, r.joined_at, r.invited_at, r.phone_at].filter((x): x is string => Boolean(x)).map((x) => new Date(x.includes('T') ? x : x.replace(' ', 'T') + 'Z').getTime()).filter((t) => !Number.isNaN(t));
      return ts.length ? Math.max(...ts) : new Date(r.updated_at).getTime();
    };
    return {
      outside: list.filter((r) => r.pin_lat != null && !r.pin_pid),
      elsewhere: list.filter((r) => r.pin_pid && !r.pin_inside_own),
      stalled: list.filter((r) => ['invited', 'joined', 'pin'].includes(r.stage) && now - lastTouch(r) > 3 * DAY),
      activeWeek: list.filter((r) => r.last_message_at && now - new Date(r.last_message_at.replace(' ', 'T') + (r.last_message_at.includes('T') ? '' : 'Z')).getTime() < 7 * DAY).length,
    };
  }, [rows]);

  if (holders.status !== 'ready') {
    return <div className="page"><StateBlock state={holders} title="Farmer roster" /></div>;
  }

  const pinInside = [...rows.values()].filter((r) => r.pin_inside_own).length;
  const runSync = async (): Promise<void> => {
    setSyncing(true);
    setSyncMsg(await sync());
    setSyncing(false);
  };

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>Onboarding monitor</h1>
          <p>{summary ? `${n(summary.holders)} farmers named on ${n(summary.farms)} khasras · ${n(Math.round(summary.farm_acres))} acres · ${summary.villages_mapped.length} villages` : ''}{lastSync ? ` · bot records synced ${fmtDate(lastSync.at)}` : ' · bot records not synced yet'}</p>
        </div>
        <span className="spacer" />
        <a className="btn-line" href="#/map" style={{ textDecoration: 'none' }}>Open the farm map</a>
        {account?.admin ? <button type="button" className="btn-primary" disabled={syncing} onClick={() => void runSync()}>{syncing ? 'Syncing…' : 'Sync from the WhatsApp bot'}</button> : null}
      </div>
      {syncMsg ? <div className="notice notice--ok">{syncMsg}</div> : null}

      <div className="tiles">
        <div className="tile"><span className="tile__label">Farmers on the record</span><span className="tile__value">{n(reached.target)}</span><span className="tile__sub">{summary ? `${n(summary.farms)} khasras · ${summary.holder_segments.marginal?.holders ? n(summary.holder_segments.marginal.holders) + ' marginal' : ''}` : ''}</span></div>
        <div className="tile"><span className="tile__label">Phone captured</span><span className="tile__value">{n(reached.phone)}</span><span className="tile__sub">{reached.target ? `${((reached.phone / reached.target) * 100).toFixed(1)}% of the record` : ''}</span></div>
        <div className="tile"><span className="tile__label">Invited on WhatsApp</span><span className="tile__value">{n(reached.invited)}</span><span className="tile__sub">link or QR shown</span></div>
        <div className="tile tile--accent"><span className="tile__label">Wrote to the bot</span><span className="tile__value">{n(reached.joined)}</span><span className="tile__sub">{n(attention.activeWeek)} active in the last 7 days</span></div>
        <div className={`tile ${pinInside < reached.pin ? 'tile--warn' : 'tile--accent'}`}><span className="tile__label">Pin inside own boundary</span><span className="tile__value">{n(pinInside)}<span className="muted" style={{ fontSize: 14 }}> / {n(reached.pin)}</span></span><span className="tile__sub">{n(attention.elsewhere.length)} in another khasra · {n(attention.outside.length + unmatched.length)} outside the record</span></div>
        <div className="tile tile--accent"><span className="tile__label">Activated</span><span className="tile__value">{n(reached.activated)}</span><span className="tile__sub">reports and alerts flowing</span></div>
        {block?.summary ? (
          <a className="tile" href="#/block" style={{ textDecoration: 'none', color: 'inherit', borderColor: 'var(--accent)' }}>
            <span className="tile__label">1,000-acre block</span>
            <span className="tile__value">{n(block.summary.farmers)}<span className="muted" style={{ fontSize: 14 }}> farmers · {n(Math.round(block.summary.acres))} ac</span></span>
            <span className="tile__sub">marginal, one acre each · {n(blockStages.phone)} reached, {n(blockStages.joined)} on WhatsApp, {n(blockStages.activated)} activated → open</span>
          </a>
        ) : null}
      </div>

      <div className="grid-2">
        <section className="card card--pad">
          <div className="card__head"><h3>Funnel — farmers who reached each stage</h3></div>
          <Funnel reached={reached} />
          <p className="card__note" style={{ marginTop: 10 }}>
            Stages 2–3 are entered by field officers here; stages 4–8 are read from the AI Bhoomi bot's own records and never guessed. A stage is never moved backwards by the sync.
          </p>
        </section>

        <section className="card card--pad">
          <div className="card__head"><h3>Needs a visit</h3><span className="spacer" /><a href="#/farmers?attention=1" className="muted" style={{ fontSize: 12 }}>all in the roster →</a></div>
          <table className="vtable">
            <thead><tr><th>Farmer</th><th>Why</th><th>When</th></tr></thead>
            <tbody>
              {attention.outside.slice(0, 6).map((r) => {
                const h = byId.get(r.holder_id);
                return (
                  <tr key={`o${r.holder_id}`} className="is-click" onClick={() => { window.location.hash = `#/map/${r.holder_id}`; }}>
                    <td>{h?.name ?? r.holder_id}<div className="muted">{h?.village}</div></td>
                    <td className="bad">Pin outside every farm{r.pin_distance_m != null ? ` · ${r.pin_distance_m} m` : ''}</td>
                    <td className="muted">{fmtDate(r.pin_at)}</td>
                  </tr>
                );
              })}
              {attention.elsewhere.slice(0, 6).map((r) => {
                const h = byId.get(r.holder_id);
                const f = r.pin_pid ? farmIndex.get(r.pin_pid) : null;
                return (
                  <tr key={`e${r.holder_id}`} className="is-click" onClick={() => { window.location.hash = `#/map/${r.holder_id}`; }}>
                    <td>{h?.name ?? r.holder_id}<div className="muted">{h?.village}</div></td>
                    <td className="warn">Pin in khasra {f?.khasra ?? r.pin_pid} — another holder's</td>
                    <td className="muted">{fmtDate(r.pin_at)}</td>
                  </tr>
                );
              })}
              {attention.stalled.slice(0, 6).map((r) => {
                const h = byId.get(r.holder_id);
                return (
                  <tr key={`s${r.holder_id}`} className="is-click" onClick={() => { window.location.hash = `#/map/${r.holder_id}`; }}>
                    <td>{h?.name ?? r.holder_id}<div className="muted">{h?.village}</div></td>
                    <td>Stopped at <StagePill stage={r.stage} small /> for 3+ days</td>
                    <td className="muted">{fmtDate(r.last_message_at ?? r.updated_at)}</td>
                  </tr>
                );
              })}
              {unmatched.slice(0, 8).map((u, i) => (
                <tr key={`u${i}${u.wa_id}`}>
                  {u.masked ? (
                    <td>WhatsApp farmer, not on the programme list<div className="muted">number ending {u.wa_id.replace(/\D/g, '')} · bot: {u.bot_state}</div></td>
                  ) : (
                    <td>{u.name || prettyPhone(u.wa_id)}<div className="muted">{prettyPhone(u.wa_id)} · bot: {u.bot_state}</div></td>
                  )}
                  <td className="bad">WhatsApp farmer not on the record{u.nearest_khasra ? ` · nearest khasra ${u.nearest_khasra}, ${u.nearest_village}, ${n(u.distance_m ?? 0)} m` : ''}</td>
                  <td className="muted">{fmtDate(u.last_message_at ?? u.created_at)}</td>
                </tr>
              ))}
              {!attention.outside.length && !attention.elsewhere.length && !attention.stalled.length && !unmatched.length ? (
                <tr><td colSpan={3} className="muted">Nothing waiting. {rows.size === 0 ? 'No farmer has been contacted yet — start on the farm map.' : ''}</td></tr>
              ) : null}
            </tbody>
          </table>
        </section>
      </div>

      <section className="card card--pad">
        <div className="card__head"><h3>By village</h3><span className="spacer" /><span className="muted" style={{ fontSize: 12 }}>{whatsapp ? `bot number ${prettyPhone(whatsapp.number)}` : ''}</span></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="vtable">
            <thead>
              <tr>
                <th>Village</th><th className="num">Farmers</th><th className="num">Khasras</th><th className="num">Acres</th>
                {STAGES.filter((s) => s !== 'target').map((s) => <th key={s} className="num" title={STAGE_LABEL[s]}>{STAGE_LABEL[s].split(' ')[0]}</th>)}
                <th className="num">Pin ✓</th><th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {villages.map((v) => (
                <tr key={v.village} className="is-click" onClick={() => { window.location.hash = `#/farmers?village=${encodeURIComponent(v.village)}`; }}>
                  <td>{v.village}</td>
                  <td className="num">{n(v.holders)}</td>
                  <td className="num">{n(v.farms)}</td>
                  <td className="num">{n(Math.round(v.acres))}</td>
                  {STAGES.filter((s) => s !== 'target').map((s) => <td key={s} className={`num ${v.reached[s] ? '' : 'muted'}`}>{v.reached[s] ? n(v.reached[s]) : '·'}</td>)}
                  <td className={`num ${v.pinElsewhere ? 'warn' : ''}`}>{v.pinInsideOwn}{v.pinElsewhere ? ` / ${v.pinInsideOwn + v.pinElsewhere}` : ''}</td>
                  <td>
                    <div className="bar" title={STAGES.map((s) => `${STAGE_LABEL[s]}: ${v.byStage[s]}`).join('\n')}>
                      {STAGES.filter((s) => s !== 'target').map((s: Stage) => (
                        <i key={s} style={{ width: `${(v.byStage[s] / Math.max(1, v.holders)) * 100}%`, background: STAGE_COLOR[s] }} />
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {summary?.villages_missing_from_extract.length ? <p className="card__note" style={{ marginTop: 8 }}>Not in the record extract, so not on this portal yet: {summary.villages_missing_from_extract.join(', ')}.</p> : null}
      </section>

      <section className="card card--pad">
        <div className="card__head"><h3>Recent activity</h3></div>
        {events.length ? (
          <ul className="events">
            {events.map((e) => {
              const h = e.holder_id ? byId.get(e.holder_id) : null;
              return (
                <li key={e.id}>
                  <span className="when">{fmtDate(e.at)} · {e.by.split('@')[0]}</span>
                  <span className="kind">{e.kind.replace('_', ' ')}</span>
                  <span>{h ? <><a href={`#/map/${h.id}`}>{h.name}</a> ({h.village}) · </> : null}{e.detail}</span>
                </li>
              );
            })}
          </ul>
        ) : <p className="muted">Nothing recorded yet.</p>}
      </section>
    </div>
  );
}
